import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, API, fileUrl, authHeaders, noteCreditsRefusal } from "@/lib/api";
import { Send, Sparkles, Mic, Square, ArrowUpRight, Loader2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { ChatBubble } from "@/components/ChatWidget";
import { errorDetail } from "@/lib/utils";

export default function AskHeda() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      english:
        "Hi, I'm Heda AI — your showroom assistant. Ask me anything about Heda Plywoods: 'I need exterior louvers for galleries', 'fire-rated panels for kitchen', 'water-resistant laminates for bathroom', or speak in Hindi, Marathi, Kannada or any language. I'll find the right products.",
      original: null,
      language: "en",
      language_label: "EN",
      referenced_products: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    const newHistory = [
      ...messages,
      { role: "user", english: q, original: q, language: "auto" },
    ];
    setMessages(newHistory);
    setSending(true);
    try {
      const { data } = await api.post("/ask-heda", {
        message: q,
        history: newHistory
          .slice(-6)
          .map((m) => ({ role: m.role, content: m.english || m.original || "" })),
      });
      setMessages((m) => [...m, { role: "assistant", ...data }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          english:
            errorDetail(e) || "Couldn't reach Heda AI right now. Please try again.",
          original: null,
          language: "en",
          language_label: "EN",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (blob) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      fd.append(
        "history",
        JSON.stringify(
          messages
            .slice(-6)
            .map((m) => ({ role: m.role, content: m.english || m.original || "" })),
        ),
      );
      const res = await fetch(`${API}/ask-heda/voice`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) {
        await noteCreditsRefusal(res);
        throw new Error(await res.text());
      }
      const data = await res.json();
      setMessages((m) => [
        ...m,
        {
          role: "user",
          english: data.transcript,
          original: data.transcript,
          language: data.language,
          language_label: data.language_label,
        },
        { role: "assistant", ...data },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          english:
            "Couldn't transcribe the voice — try again or type the question.",
          original: null,
          language: "en",
          language_label: "EN",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mx-auto flex h-[calc(100dvh-220px)] min-h-[420px] max-w-3xl flex-col">
        <div className="mb-4">
          <div className="hed-overline">Ask Heda</div>
          <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink md:text-[30px]">
            Anything across your whole catalog.
          </h1>
          <p className="mt-1 text-[13px] text-heda-sub">
            Type or speak in any language. Heda AI searches every product, every PDF, every research note — and recommends the right match with a tap-to-open link.
          </p>
        </div>

        <div
          ref={scrollRef}
          className="heda-panel mb-3 flex-1 space-y-3 overflow-y-auto p-4"
        >
          {messages.map((m, idx) => (
            <div key={idx}>
              <ChatBubble message={m} />
              {m.role === "assistant" && m.referenced_products?.length > 0 && (
                <div className="mt-2 ml-1 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {m.referenced_products.map((p) => (
                    <Link
                      key={p.id}
                      to={p.code ? `/admin/products/${p.id}/edit` : `/admin/products/${p.id}/edit`}
                      className="group flex items-center gap-2 rounded-xl border border-heda-line bg-white p-2 transition hover:border-heda-gold"
                      data-testid={`ask-heda-product-card-${p.id}`}
                    >
                      {p.thumb ? (
                        <img
                          src={fileUrl(p.thumb)}
                          alt={p.name}
                          className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-heda-gold-tint">
                          <Sparkles size={14} className="text-heda-gold" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-heda-ink truncate">
                          {p.name}
                        </div>
                        <div className="truncate text-[10px] uppercase tracking-wider text-heda-mute">
                          {p.category || p.code || "View →"}
                        </div>
                      </div>
                      <ArrowUpRight
                        size={12}
                        className="flex-shrink-0 text-heda-mute group-hover:text-heda-gold"
                      />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="hed-typing bg-heda-cream border border-heda-line px-4 py-3 rounded-2xl rounded-bl-md">
                <span></span><span></span><span></span>
              </div>
            </div>
          )}
        </div>

        <form
          className="flex items-center gap-2 rounded-full border border-heda-line bg-white p-1.5 shadow-heda focus-within:border-heda-gold"
          onSubmit={(e) => { e.preventDefault(); send(); }}
        >
          <AskMicButton onAudio={sendVoice} disabled={sending} />
          <input
            data-testid="ask-heda-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Tell me what you need…"
            className="min-w-0 flex-1 bg-transparent px-2 py-2 text-[15px] outline-none placeholder:text-heda-mute"
          />
          <button
            data-testid="ask-heda-send"
            type="submit"
            disabled={sending || !input.trim()}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-heda-ink text-white transition hover:bg-black disabled:opacity-40"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    </AdminLayout>
  );
}

function AskMicButton({ onAudio, disabled }) {
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size > 1000) onAudio(blob);
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      alert("Microphone access denied. Allow microphone access to use voice search.");
    }
  };
  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    setRecording(false);
  };

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled && !recording}
      data-testid={recording ? "ask-heda-mic-stop" : "ask-heda-mic-start"}
      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition disabled:opacity-40 ${
        recording
          ? "animate-pulse bg-red-700 text-white hover:bg-red-800"
          : "border border-heda-line bg-heda-cream text-heda-ink hover:border-heda-gold hover:text-heda-gold"
      }`}
    >
      {recording ? <Square size={14} /> : <Mic size={16} />}
    </button>
  );
}
